package songwritersnotebook.app.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.billing.*
import songwritersnotebook.app.getPlatform
import songwritersnotebook.app.utils.PlatformUtils
import songwritersnotebook.app.utils.getPlatformContext
import songwritersnotebook.app.viewmodels.MainViewModel
import kotlinx.coroutines.launch
import kotlin.math.roundToInt

import songwritersnotebook.app.components.WebNavHeader
import songwritersnotebook.app.components.WebFooter

data class TierDefinition(
    val tier: SubscriptionTier,
    val title: String,
    val productId: String,
    val features: List<String>,
    val highlightColor: Color? = null
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SubscriptionScreen(
    onBack: () -> Unit,
    onFeatures: () -> Unit = {},
    onPricing: () -> Unit = {},
    onRoadmap: () -> Unit = {},
    onHome: () -> Unit = {},
    onPrivacy: () -> Unit = {},
    onTerms: () -> Unit = {},
    onOpenUrl: (String) -> Unit = {},
    onSignIn: () -> Unit,
    mainViewModel: MainViewModel
) {
    val productDetailsState by mainViewModel.productDetailsState.collectAsState()
    val activeSubscription by mainViewModel.activeSubscription.collectAsState()
    val userProfile by mainViewModel.userProfile.collectAsState()
    val billingError by mainViewModel.billingError.collectAsState()
    val currentUser by mainViewModel.currentUser.collectAsState()
    val isBillingProcessing by mainViewModel.billingClientWrapper.isProcessing.collectAsState()
    
    val isWeb = remember { getPlatform().name.contains("Web", ignoreCase = true) }
    val platformUtils = remember { PlatformUtils() }
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    var isProcessingPortal by remember { mutableStateOf(false) }

    LaunchedEffect(billingError) {
        billingError?.let { error ->
            snackbarHostState.showSnackbar(
                message = error,
                duration = SnackbarDuration.Long
            )
            mainViewModel.clearBillingError()
        }
    }

    var selectedTab by remember { mutableStateOf("Yearly") }

    val context = getPlatformContext()
    
    val onSubscribeClicked: (ProductInfo, String) -> Unit = { productInfo, basePlanId ->
        val userId = mainViewModel.currentUser.value?.uid
        if (userId == null) {
            onSignIn()
        } else {
            mainViewModel.billingClientWrapper.launchPurchaseFlow(context, productInfo, userId, basePlanId)
        }
    }

    val hobbyistTier = TierDefinition(
        tier = SubscriptionTier.HOBBYIST,
        title = "Hobbyist - Feature Unlock",
        productId = BillingClientWrapper.TIER_HOBBYIST_ID,
        features = (if (isWeb) listOf("Available on Mobile Only") else emptyList()) + listOf(
            "Collaborate on up to 5 songs owned by Studio users",
            "500 AI Credits (One-time)",
            "No Ads",
            "Local Storage Only",
            "Text + Audio Export",
            "Guitar Tabs",
            "Version History"
        ),
        highlightColor = MaterialTheme.colorScheme.secondary
    )

    val studioTier = TierDefinition(
        tier = SubscriptionTier.STUDIO,
        title = "Studio - Cloud Subscription",
        productId = BillingClientWrapper.TIER_STUDIO_ID,
        features = listOf(
            "Collaborate on unlimited shared songs",
            "Invite 1 Free & 5 Hobbyist users per song",
            "Invite unlimited Studio users",
            "Unlimited AI Generation*", 
            "10 GB Cloud Storage & Sync", 
            "No Ads", 
            "Text + Audio Export", 
            "Guitar Tabs", 
            "Version History"
        )
    )

    val freeTier = TierDefinition(
        tier = SubscriptionTier.FREE,
        title = "Basic - Free",
        productId = "free",
        features = if (isWeb) {
            listOf(
                "Try out features for free",
                "Collaborate on 1 shared song",
                "Basic Song Editor (no tabs or version history)",
                "Ad Supported"
            )
        } else {
            listOf(
                "Unlimited local songs",
                "Collaborate on 1 shared song",
                "Basic Song Editor (no tabs or version history)",
                "Standard Audio Recording",
                "Ad Supported"
            )
        }
    )

    Scaffold(
        snackbarHost = { SnackbarHost(hostState = snackbarHostState) },
        topBar = {
            if (isWeb) {
                WebNavHeader(
                    mainViewModel = mainViewModel,
                    onSignIn = onSignIn,
                    onFeatures = onFeatures,
                    onPricing = onPricing,
                    onRoadmap = onRoadmap,
                    onHome = onHome,
                    selectedRoute = "pricing"
                )
            } else {
                TopAppBar(
                    title = { Text("Upgrade to Premium") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    },
                    actions = {
                        if (!isWeb) {
                            TextButton(onClick = { mainViewModel.restorePurchases() }) {
                                Text("Restore")
                            }
                        }
                    }
                )
            }
        }
    ) { paddingValues ->

        when (productDetailsState.loadingState) {
            LoadingState.LOADING -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                    Text("Loading plans...", modifier = Modifier.padding(top = 64.dp))
                }
            }
            LoadingState.ERROR -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = productDetailsState.errorMessage ?: "An unknown error occurred.",
                        color = MaterialTheme.colorScheme.error,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(16.dp)
                    )
                }
            }
            LoadingState.SUCCESS -> {
                BoxWithConstraints(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
                    val isWide = maxWidth > 900.dp
                    val contentWidth = if (isWide) androidx.compose.ui.unit.min(1100.dp, maxWidth) else if (isWeb) androidx.compose.ui.unit.min(600.dp, maxWidth) else maxWidth
                    
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        item {
                            Column(
                                modifier = Modifier
                                    .widthIn(max = contentWidth)
                                    .padding(horizontal = 16.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Spacer(modifier = Modifier.height(if (isWide) 64.dp else 32.dp))
                                Text(
                                    "Elevate Your Songwriting",
                                    style = if (isWide) MaterialTheme.typography.displaySmall else MaterialTheme.typography.headlineMedium,
                                    fontWeight = FontWeight.Black,
                                    textAlign = TextAlign.Center
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                                Text(
                                    "Choose the plan that fits your creative workflow.",
                                    style = MaterialTheme.typography.bodyLarge,
                                    textAlign = TextAlign.Center,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    modifier = Modifier.widthIn(max = 600.dp)
                                )
                                Spacer(modifier = Modifier.height(32.dp))

                                if (activeSubscription != SubscriptionTier.FREE) {
                                    OutlinedButton(
                                        modifier = Modifier.fillMaxWidth(if (isWeb && !isWide) 0.5f else if (isWide) 0.3f else 1f),
                                        enabled = !isProcessingPortal && !isBillingProcessing,
                                        onClick = {
                                            val platform = userProfile?.subscriptionPlatform
                                            when (platform) {
                                                "stripe" -> {
                                                    scope.launch {
                                                        isProcessingPortal = true
                                                        val url = songwritersnotebook.app.utils.CloudFunctionService.createStripePortalSession()
                                                        if (url != null) {
                                                            platformUtils.openUrl(url)
                                                        } else {
                                                                snackbarHostState.showSnackbar("Unable to open billing portal. Please try again.")
                                                        }
                                                        isProcessingPortal = false
                                                    }
                                                }
                                                "google_play" -> {
                                                    platformUtils.openUrl("https://play.google.com/store/account/subscriptions")
                                                }
                                                "apple" -> {
                                                    platformUtils.openUrl("https://apps.apple.com/account/subscriptions")
                                                }
                                                else -> {
                                                    if (isWeb) {
                                                        scope.launch {
                                                            isProcessingPortal = true
                                                            val url = songwritersnotebook.app.utils.CloudFunctionService.createStripePortalSession()
                                                            if (url != null) {
                                                                platformUtils.openUrl(url)
                                                            } else {
                                                                platformUtils.openUrl("https://billing.stripe.com/p/login/live_1T19LB9u04zOJSewZpNRZNRp")
                                                            }
                                                            isProcessingPortal = false
                                                        }
                                                    } else {
                                                        platformUtils.openUrl("https://play.google.com/store/account/subscriptions")
                                                    }
                                                }
                                            }
                                        }
                                    ) {
                                        if (isProcessingPortal) {
                                            CircularProgressIndicator(
                                                modifier = Modifier.size(16.dp),
                                                strokeWidth = 2.dp,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                            Spacer(modifier = Modifier.width(8.dp))
                                        }
                                        Text("Manage Subscription")
                                    }
                                    Spacer(modifier = Modifier.height(16.dp))
                                }

                                BillingPeriodToggle(
                                    selectedTab = selectedTab,
                                    onTabSelected = { selectedTab = it },
                                    productDetailsList = productDetailsState.productDetailsList,
                                    isWeb = isWeb
                                )
                                
                                LaunchedEffect(productDetailsState.productDetailsList, selectedTab) {
                                    val studioProduct = productDetailsState.productDetailsList.find { it.productId == studioTier.productId }
                                    val offers = studioProduct?.subscriptionOfferDetails?.map { it.basePlanId } ?: emptyList()
                                    println("DEBUG_BILLING: Tab=$selectedTab, Products=${productDetailsState.productDetailsList.map { it.productId }}, StudioOffers=$offers")
                                }
                                
                                Spacer(modifier = Modifier.height(48.dp))
                            }
                        }

                        if (selectedTab == "Monthly" || selectedTab == "Yearly") {
                            item {
                                if (isWide) {
                                    Row(
                                        modifier = Modifier
                                            .widthIn(max = contentWidth)
                                            .padding(horizontal = 16.dp)
                                            .height(IntrinsicSize.Max),
                                        horizontalArrangement = Arrangement.spacedBy(24.dp)
                                    ) {
                                        Box(modifier = Modifier.weight(1f)) {
                                            SubscriptionTierCard(
                                                tierDefinition = freeTier,
                                                productDetails = null,
                                                targetBasePlanId = null,
                                                isCurrentPlan = activeSubscription == SubscriptionTier.FREE,
                                                isProcessing = isBillingProcessing || isProcessingPortal,
                                                onSubscribeClicked = { _, _ -> },
                                                isWeb = isWeb
                                            )
                                        }
                                        
                                        Box(modifier = Modifier.weight(1f)) {
                                            val hobbyistProduct = productDetailsState.productDetailsList.find { it.productId == hobbyistTier.productId }
                                            SubscriptionTierCard(
                                                tierDefinition = hobbyistTier,
                                                productDetails = hobbyistProduct,
                                                targetBasePlanId = null,
                                                isCurrentPlan = activeSubscription == SubscriptionTier.HOBBYIST,
                                                isProcessing = isBillingProcessing || isProcessingPortal,
                                                onSubscribeClicked = onSubscribeClicked,
                                                isWeb = isWeb
                                            )
                                        }

                                        Box(modifier = Modifier.weight(1f)) {
                                            val productDetails = productDetailsState.productDetailsList.find { it.productId == studioTier.productId }
                                            val duration = if (selectedTab == "Yearly") "yearly" else "monthly"
                                            val targetBasePlanId = "studio-$duration"
                                            val isCurrentPlan = activeSubscription == studioTier.tier

                                            SubscriptionTierCard(
                                                tierDefinition = studioTier,
                                                productDetails = productDetails,
                                                targetBasePlanId = targetBasePlanId,
                                                isCurrentPlan = isCurrentPlan,
                                                isProcessing = isBillingProcessing || isProcessingPortal,
                                                onSubscribeClicked = onSubscribeClicked,
                                                isWeb = isWeb
                                            )
                                        }
                                    }
                                } else {
                                    Column(
                                        modifier = Modifier.widthIn(max = contentWidth).padding(horizontal = 16.dp),
                                        verticalArrangement = Arrangement.spacedBy(24.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        val hobbyistProduct = productDetailsState.productDetailsList.find { it.productId == hobbyistTier.productId }
                                        SubscriptionTierCard(
                                            tierDefinition = hobbyistTier,
                                            productDetails = hobbyistProduct,
                                            targetBasePlanId = null,
                                            isCurrentPlan = activeSubscription == SubscriptionTier.HOBBYIST,
                                            isProcessing = isBillingProcessing || isProcessingPortal,
                                            onSubscribeClicked = onSubscribeClicked,
                                            isWeb = isWeb
                                        )

                                        val productDetails = productDetailsState.productDetailsList.find { it.productId == studioTier.productId }
                                        val duration = if (selectedTab == "Yearly") "yearly" else "monthly"
                                        val targetBasePlanId = "studio-$duration"
                                        val isCurrentPlan = activeSubscription == studioTier.tier

                                        SubscriptionTierCard(
                                            tierDefinition = studioTier,
                                            productDetails = productDetails,
                                            targetBasePlanId = targetBasePlanId,
                                            isCurrentPlan = isCurrentPlan,
                                            isProcessing = isBillingProcessing || isProcessingPortal,
                                            onSubscribeClicked = onSubscribeClicked,
                                            isWeb = isWeb
                                        )
                                    }
                                }
                            }

                            item {
                                Column(
                                    modifier = Modifier.widthIn(max = contentWidth).padding(16.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Spacer(modifier = Modifier.height(48.dp))
                                    Text(
                                        "Recurring billing for subscriptions, cancel anytime.",
                                        modifier = Modifier.fillMaxWidth(),
                                        textAlign = TextAlign.Center,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.outline
                                    )
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text(
                                        "*Unlimited AI Generation is subject to a fair-use policy of 100 requests per day to prevent abuse.",
                                        modifier = Modifier.fillMaxWidth(),
                                        textAlign = TextAlign.Center,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.outline
                                    )
                                    
                                    if (isWeb) {
                                        Spacer(modifier = Modifier.height(100.dp))
                                    }
                                }
                            }
                        } else if (selectedTab == "AI Credits") {
                            item {
                                Column(
                                    modifier = Modifier.widthIn(max = contentWidth).padding(horizontal = 16.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        "Purchase non-expiring AI credits for use with the Harmony AI Assistant.",
                                        style = MaterialTheme.typography.bodyLarge,
                                        textAlign = TextAlign.Center,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Spacer(modifier = Modifier.height(16.dp))

                                    val currentCredits = userProfile?.aiCreditsOneTime ?: 0
                                    Surface(
                                        color = MaterialTheme.colorScheme.secondaryContainer,
                                        shape = RoundedCornerShape(12.dp)
                                    ) {
                                        Text(
                                            "Current Balance: $currentCredits Credits",
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.onSecondaryContainer,
                                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                                        )
                                    }
                                    
                                    Spacer(modifier = Modifier.height(32.dp))

                                    val aiProducts = productDetailsState.productDetailsList
                                            .filter { BillingClientWrapper.AI_CREDIT_PRODUCT_IDS.contains(it.productId) }
                                            .sortedBy { productDetail ->
                                                BillingClientWrapper.AI_CREDIT_PRODUCT_IDS.indexOf(productDetail.productId)
                                            }
                                    
                                    if (isWide) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Max),
                                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                                        ) {
                                            aiProducts.forEach { productDetails ->
                                                Box(modifier = Modifier.weight(1f)) {
                                                    AiCreditPackCard(
                                                        productDetails = productDetails,
                                                        isProcessing = isBillingProcessing || isProcessingPortal,
                                                        onPurchaseClicked = {
                                                            val userId = mainViewModel.currentUser.value?.uid
                                                            if (userId == null) {
                                                                onSignIn()
                                                            } else {
                                                                mainViewModel.billingClientWrapper.launchPurchaseFlow(context, productDetails, userId)
                                                            }
                                                        },
                                                        isVertical = true
                                                    )
                                                }
                                            }
                                        }
                                    } else {
                                        aiProducts.forEach { productDetails ->
                                            AiCreditPackCard(
                                                productDetails = productDetails,
                                                isProcessing = isBillingProcessing || isProcessingPortal,
                                                onPurchaseClicked = {
                                                    val userId = mainViewModel.currentUser.value?.uid
                                                    if (userId == null) {
                                                        onSignIn()
                                                    } else {
                                                        mainViewModel.billingClientWrapper.launchPurchaseFlow(context, productDetails, userId)
                                                    }
                                                }
                                            )
                                            Spacer(modifier = Modifier.height(16.dp))
                                        }
                                    }
                                    
                                    if (isWeb) {
                                        Spacer(modifier = Modifier.height(200.dp))
                                    }
                                }
                            }
                        }

                        if (isWeb) {
                            item {
                                WebFooter(
                                    onNavigateToHome = onHome,
                                    onNavigateToFeatures = onFeatures,
                                    onNavigateToPricing = onPricing,
                                    onNavigateToRoadmap = onRoadmap,
                                    onNavigateToPrivacy = onPrivacy,
                                    onNavigateToTerms = onTerms,
                                    onOpenUrl = onOpenUrl,
                                    isCompact = false
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BillingPeriodToggle(
    selectedTab: String,
    onTabSelected: (String) -> Unit,
    productDetailsList: List<ProductInfo>,
    isWeb: Boolean
) {
    val tabs = listOf("Monthly", "Yearly", "AI Credits")
    
    TabRow(
        selectedTabIndex = tabs.indexOf(selectedTab),
        containerColor = Color.Transparent,
        contentColor = MaterialTheme.colorScheme.primary,
        divider = {},
        indicator = { tabPositions ->
            TabRowDefaults.SecondaryIndicator(
                modifier = Modifier.tabIndicatorOffset(tabPositions[tabs.indexOf(selectedTab)]),
                color = MaterialTheme.colorScheme.primary
            )
        }
    ) {
        tabs.forEach { tab ->
            Tab(
                selected = selectedTab == tab,
                onClick = { onTabSelected(tab) },
                text = {
                    Text(
                        text = tab,
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = if (selectedTab == tab) FontWeight.Bold else FontWeight.Normal
                    )
                }
            )
        }
    }
}

@Composable
private fun SubscriptionTierCard(
    tierDefinition: TierDefinition,
    productDetails: ProductInfo?,
    targetBasePlanId: String?,
    isCurrentPlan: Boolean,
    isProcessing: Boolean,
    onSubscribeClicked: (ProductInfo, String) -> Unit,
    isWeb: Boolean = false
) {
    val highlightColor = tierDefinition.highlightColor ?: MaterialTheme.colorScheme.primary
    val isStudio = tierDefinition.tier == SubscriptionTier.STUDIO
    val isHobbyist = tierDefinition.tier == SubscriptionTier.HOBBYIST
    val isFree = tierDefinition.tier == SubscriptionTier.FREE
    
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (isWeb) Modifier.heightIn(min = 550.dp) else Modifier),
        shape = RoundedCornerShape(24.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (isStudio) 
                MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.1f)
            else if (isHobbyist)
                MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.1f)
            else
                MaterialTheme.colorScheme.surface
        ),
        border = BorderStroke(
            width = if (isStudio) 2.dp else 1.dp,
            color = if (isStudio) highlightColor else MaterialTheme.colorScheme.outlineVariant
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isStudio) 8.dp else 0.dp)
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .padding(vertical = 32.dp, horizontal = 24.dp)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = tierDefinition.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = highlightColor
                )
                
                Spacer(modifier = Modifier.height(16.dp))
                
                if (productDetails != null || isFree) {
                    val priceText = if (isFree) {
                        "$0"
                    } else if (isHobbyist) {
                        productDetails?.oneTimePurchaseOfferDetails?.formattedPrice ?: "---"
                    } else {
                        productDetails?.subscriptionOfferDetails?.find { it.basePlanId == targetBasePlanId }
                            ?.pricingPhases?.pricingPhaseList?.firstOrNull()?.formattedPrice ?: "---"
                    }
                    
                    Text(
                        text = priceText,
                        style = MaterialTheme.typography.displayMedium,
                        fontWeight = FontWeight.Black
                    )
                    Text(
                        text = if (isFree) "always free" else if (isHobbyist) "one-time purchase" else if (targetBasePlanId?.contains("yearly") == true) "per year" else "per month",
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                } else {
                    Text(
                        text = if (isHobbyist && isWeb) "Mobile Only" else "---",
                        style = if (isHobbyist && isWeb) MaterialTheme.typography.displaySmall else MaterialTheme.typography.displayMedium,
                        fontWeight = FontWeight.Black
                    )
                }

                Spacer(modifier = Modifier.height(32.dp))

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    tierDefinition.features.forEach { feature ->
                        FeatureText(text = feature)
                    }
                }

                Spacer(modifier = Modifier.height(32.dp))

                if (isCurrentPlan) {
                    OutlinedButton(
                        onClick = {},
                        enabled = false,
                        modifier = Modifier.fillMaxWidth(),
                        colors = ButtonDefaults.outlinedButtonColors(
                            disabledContentColor = MaterialTheme.colorScheme.primary
                        )
                    ) {
                        Text("Current Plan")
                    }
                } else if (isFree) {
                    // No button for free
                } else if (isHobbyist && isWeb) {
                    Button(
                        onClick = {},
                        enabled = false,
                        modifier = Modifier.fillMaxWidth(),
                        colors = ButtonDefaults.buttonColors(
                            disabledContainerColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
                        )
                    ) {
                        Text("Available on Android", color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f))
                    }
                } else {
                    Button(
                        onClick = {
                            if (productDetails != null) {
                                onSubscribeClicked(productDetails, targetBasePlanId ?: "")
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = (productDetails != null || isFree) && !isProcessing,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = if (isStudio) highlightColor else MaterialTheme.colorScheme.primary
                        )
                    ) {
                        if (isProcessing) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                        }
                        Text(if (productDetails == null && !isFree) "Unavailable" else if (isHobbyist) "Buy Now" else "Subscribe")
                    }
                }
            }

            if (isHobbyist) {
                 Surface(
                    color = MaterialTheme.colorScheme.secondary,
                    shape = RoundedCornerShape(bottomStart = 8.dp, bottomEnd = 8.dp),
                    modifier = Modifier
                        .align(Alignment.TopCenter)
                        .padding(horizontal = 16.dp)
                ) {
                    Text(
                        "BEST VALUE",
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSecondary
                    )
                }
            } else if (isStudio) {
                Surface(
                    color = highlightColor,
                    shape = RoundedCornerShape(bottomStart = 8.dp, bottomEnd = 8.dp),
                    modifier = Modifier
                        .align(Alignment.TopCenter)
                        .padding(horizontal = 16.dp)
                ) {
                    Text(
                        "RECOMMENDED",
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimary
                    )
                }
            }
        }
    }
}

@Composable
private fun FeatureText(text: String) {
    Row(
        verticalAlignment = Alignment.Top,
        modifier = Modifier.padding(bottom = 4.dp)
    ) {
        Icon(
            Icons.Default.Check,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(18.dp).padding(top = 2.dp)
        )
        Spacer(modifier = Modifier.width(12.dp))
        Text(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            lineHeight = 20.sp
        )
    }
}

@Composable
private fun AiCreditPackCard(
    productDetails: ProductInfo,
    isProcessing: Boolean,
    onPurchaseClicked: () -> Unit,
    isVertical: Boolean = false
) {
    Card(
        modifier = Modifier.fillMaxWidth().then(if (isVertical) Modifier.fillMaxHeight() else Modifier),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        if (isVertical) {
            Column(
                modifier = Modifier.padding(24.dp).fillMaxSize(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = productDetails.name,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = productDetails.description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center
                    )
                }
                
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = productDetails.oneTimePurchaseOfferDetails?.formattedPrice ?: "---",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Black,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Button(
                        onClick = onPurchaseClicked,
                        enabled = !isProcessing,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        if (isProcessing) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                        } else {
                            Text("Buy Credits")
                        }
                    }
                }
            }
        } else {
            Row(
                modifier = Modifier
                    .padding(24.dp)
                    .fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = productDetails.name,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = productDetails.description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        text = productDetails.oneTimePurchaseOfferDetails?.formattedPrice ?: "---",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Black,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    Button(
                        onClick = onPurchaseClicked,
                        enabled = !isProcessing,
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        if (isProcessing) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                        } else {
                            Text("Buy")
                        }
                    }
                }
            }
        }
    }
}
